<template>
    <div
        class="room-plan-table table"
        :class="[tableShapeClass, tableStatusClass, disabled ? 'disabled' : '']"
        :style="elementStyle"
        @click="onElementClick"
    >
        <div class="inner">
            <div v-if="isRectangleTable" class="tablename text-truncate">{{ table.name }}</div>
            <div class="additionalinfo">
                <div v-if="!isRectangleTable" class="tablename text-truncate">{{ table.name }}</div>
                <div class="usercount itemrow">
                    <i class="fal fa-user mr-1"></i>{{ table.manualEndUsersCount ?? table.allocations }}
                </div>
                <div v-if="table.timestamp" class="timestamp itemrow">{{ DateTime.toTimeString(table.timestamp) }}</div>
                <div v-if="table.openBillAmount" class="amount itemrow">{{ toMoney(table.openBillAmount) }}</div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
    import { TableShape } from "@/data/model/Enums";
    import { type IRoomPlanTable, RoomPlanTableStatus } from "@/data/model/Operator";
    import { DateTime } from "@/data/util/DateTime";
    import { toMoney } from "@/shared/utils/format";
    import { computed } from "vue";

    const props = defineProps<{
        table: IRoomPlanTable;
        disabled: boolean;
    }>();

    const tableShapeClass = computed(() => TableShape[props.table.shape].toLowerCase());
    const tableStatusClass = computed(() => RoomPlanTableStatus[props.table.status].toLowerCase());
    const isRectangleTable = computed(() => props.table.shape == TableShape.Rectangle);
    const elementStyle = computed(() => {
        return {
            width: props.table.dimension!.width + "%",
            height: props.table.dimension!.height + "%",
            top: props.table.dimension!.posY + "%",
            left: props.table.dimension!.posX + "%",
        };
    });

    const emit = defineEmits(["tableSelected"]);

    function onElementClick() {
        if (!props.disabled) {
            emit("tableSelected", props.table);
        }
    }
</script>

<style lang="scss">
    @use "./../../../assets/styles/scss/shared/_colors.scss";
    @use "./../../../assets/styles/scss/shared/_variables.scss";

    .room-plan-table {
        position: absolute;
        background-color: #c9c9c9;
        border-radius: 5px;
        overflow: hidden;
        -webkit-box-shadow: 1px 1px 1px 0px rgba(87, 87, 87, 0.8);
        -moz-box-shadow: 1px 1px 1px 0px rgba(87, 87, 87, 0.8);
        box-shadow: 1px 1px 1px 0px rgba(87, 87, 87, 0.8);
        transition: background-color 0.3s variables.$easeOutExpo;
        padding: 6px;
        font-family: Poppins, Helvetica, "Open Sans", sans-serif;
        cursor: pointer;

        > .inner {
            height: 100%;
            width: 100%;
            font-size: 12px;
            color: colors.$black;

            flex-direction: column;
            display: flex;

            > .tablename {
                min-height: 1.25em;
            }

            > .additionalinfo {
                margin: auto;
                text-align: center;

                > .fa-user {
                    margin-top: 2px;
                    margin-right: 2px;
                }

                .usercount {
                    margin-top: 0.25em;
                }

                .timestamp {
                    margin-top: 0.25em;
                }

                .timestamp,
                .amount {
                    line-height: 1.1em;
                }
            }
        }

        &.round {
            border-radius: 50%;
            justify-content: center;
        }

        &.openbill {
            background-color: colors.$red;
        }

        &.unfulfilledorder {
            background-color: colors.$fontColor;

            .inner {
                color: colors.$white;
            }
        }

        &.noordermade {
            background-color: colors.$orange;
        }

        &.disabled {
            opacity: 0.2;
            cursor: default;
        }
    }
</style>
